"use client"

import {useState, useEffect} from "react"
import axiosInstance from "@/api/axios"

// API integration utility for cost savings components
export interface CostSavingsForecastParams {
    period?: "quarterly" | "6months" | "yearly"
    waste_type?: string
    bin_id?: number
    historical_days?: number
    scenarios?: boolean
    forecast_years?: number
    confidence_threshold?: number
}

export interface CostSavingsForecastResponse {
    status: string
    data: {
        forecast_period: string
        forecast_months: number
        historical_analysis: {
            days_analyzed: number
            total_records: number
            date_range: {
                start: string
                end: string
            }
        }
        waste_types: Array<{
            waste_type: string
            trend_analysis: {
                slope: number
                trend_direction: number
                trend_label: string
                confidence: number
                r_squared: number
            }
            historical_summary: {
                total_cost_savings: number
                daily_average_savings: number
                records_count: number
                valid_records_count: number
            }
            forecast: {
                projected_cost_savings: number
                monthly_breakdown: Array<{
                    month: number
                    projected_savings: number
                }>
            }
        }>
        scenarios?: {
            realistic: {
                name: string
                description: string
                yearly_projections: Array<{
                    year: number
                    total_cost_savings: number
                }>
            }
            optimistic: {
                name: string
                description: string
                yearly_projections: Array<{
                    year: number
                    total_cost_savings: number
                }>
            }
            pessimistic: {
                name: string
                description: string
                yearly_projections: Array<{
                    year: number
                    total_cost_savings: number
                }>
            }
        }
        summary: {
            total_projected_savings: number
            dominant_trend: string
            high_confidence_predictions: number
            processed_waste_types: number
            skipped_waste_types: number
        }
        skipped_waste_types?: Array<{
            waste_type: string
            reason: string
            days_available: number
        }>
    }
    warnings?: string[]
}

export const useCostSavingsForecast = (params: CostSavingsForecastParams = {}) => {
    const [data, setData] = useState<CostSavingsForecastResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchData = async () => {
        try {
            setLoading(true)
            setError(null)

            // Build query parameters
            const queryParams = new URLSearchParams()
            if (params.period) queryParams.append("period", params.period)
            if (params.waste_type) queryParams.append("waste_type", params.waste_type)
            if (params.bin_id) queryParams.append("bin_id", params.bin_id.toString())
            if (params.historical_days) queryParams.append("historical_days", params.historical_days.toString())
            if (params.scenarios !== undefined) queryParams.append("scenarios", params.scenarios.toString())
            if (params.forecast_years) queryParams.append("forecast_years", params.forecast_years.toString())
            if (params.confidence_threshold)
                queryParams.append("confidence_threshold", params.confidence_threshold.toString())

            // Make API call with axiosInstance
            const response = await axiosInstance.get(`cost_savings_forecast?${queryParams.toString()}`)
            setData(response.data)
        } catch (err) {
            setError(err instanceof Error ? err.message : "An unknown error occurred")
            console.error("Cost savings forecast API error:", err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [JSON.stringify(params)]) // Re-fetch when params change

    return {
        data,
        loading,
        error,
        refetch: fetchData,
    }
}

// Utility function to transform API data for different chart formats
export const transformDataForScenarioChart = (apiData: CostSavingsForecastResponse) => {
    if (!apiData.data.scenarios) return null

    const {realistic, optimistic, pessimistic} = apiData.data.scenarios

    return {
        years: realistic.yearly_projections.map((p) => p.year),
        historical: realistic.yearly_projections.filter((p) => p.year <= new Date().getFullYear()),
        scenarios: {
            realistic: realistic.yearly_projections,
            optimistic: optimistic.yearly_projections,
            pessimistic: pessimistic.yearly_projections,
        },
    }
}

export const transformDataForCategoryChart = (apiData: CostSavingsForecastResponse) => {
    const wasteTypes = apiData.data.waste_types
    const startYear = 2018
    const endYear = 2030
    const totalYears = endYear - startYear + 1

    const transformedData: Array<{
        Year: number
        Category: string
        "Cost Savings": number
    }> = []

    wasteTypes.forEach((wasteType) => {
        const categoryName = wasteType.waste_type.charAt(0).toUpperCase() + wasteType.waste_type.slice(1)
        const yearlyGrowth = wasteType.forecast.projected_cost_savings / totalYears

        for (let year = startYear; year <= endYear; year++) {
            const yearIndex = year - startYear
            const baseSavings = yearlyGrowth * 0.3
            const growthFactor = yearIndex / totalYears
            const savings = baseSavings + yearlyGrowth * growthFactor

            transformedData.push({
                Year: year,
                Category: categoryName,
                "Cost Savings": Math.max(0, savings),
            })
        }
    })

    return transformedData
}
